/**
 * Wave 45 · Inbox envelope in TopBar.
 *
 * Renders an envelope icon with a red unread badge. Click opens a
 * dropdown panel that lists recent inbox messages (user DMs, system
 * notices, agent_alerts all unified). Click a message to mark it read;
 * "Marcar todas como lidas" at the bottom. Envelope fetches the unread
 * count every 30s via polling (cheap D1 COUNT), panel fetches the full
 * list lazily on open.
 *
 * Design choices:
 *   - Envelope icon matches the existing search/theme buttons in
 *     TopBar (same `hover:bg-surface-3`, `focus-visible:ring-2`).
 *   - Badge is a small red dot with count when >0 — pulse animation
 *     is subtle (scale 1.0 → 1.04 → 1.0, 1.2s ease-in-out). Capped at
 *     99+ so the layout never blows up.
 *   - Dropdown uses `transform-origin: top right` + scale-in, matching
 *     the account menu already in TopBar.
 *   - Each row shows author, relative time, kind badge (only for
 *     agent_alert + system — user messages look like a normal DM).
 *   - Panel closes on outside click + ESC.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/store/auth.store';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { messageBodyPlain } from '@/lib/message-body';
import { Compose } from './Compose';
import { Thread } from './Thread';
// PM-friction audit · 2026-05-20 — Skeleton substitui "Carregando…"
// raw no dropdown da sineta. PM abre o sino o tempo todo; texto solto
// dava sensação de stuck quando a rede demora.
import { Skeleton } from '@/components/ui/Skeleton';
import { parseServerDate } from '@/lib/parse-server-date';
import { useT } from '@/hooks/useT';

type InboxEntry = Awaited<ReturnType<typeof api.listInbox>>[number];
type Folder = 'inbox' | 'sent' | 'deleted';

// Wave 39.B · aumentado de 30s → 60s pra reduzir 429s no topbar.
// Badge de inbox não precisa de sub-minuto accuracy.
const POLL_INTERVAL_MS = 60_000;

export function InboxBell() {
  const user = useAuth((s) => s.user);
  const { locale } = useT();
  const isEn = locale === 'en';
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [folder, setFolder] = useState<Folder>('inbox');
  const [entries, setEntries] = useState<InboxEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Poll unread count. Keep it cheap: COUNT(*) on unread rows, no joins.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      try {
        const { unread: n } = await api.getUnreadCount();
        if (alive) setUnread(n);
      } catch {
        // Offline or expired token — silently ignore. The envelope will
        // catch up on the next tick once connectivity returns.
      }
    };
    void tick();
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [user]);

  // Outside-click + ESC to close. Same pattern as the account menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const loadInbox = useCallback(
    async (f: Folder = folder) => {
      setLoading(true);
      try {
        const list = await api.listInbox({ folder: f });
        setEntries(list);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    },
    [folder],
  );

  // When the panel opens for the first time, fetch the list. Subsequent
  // opens reuse the cached list unless the user explicitly refreshes —
  // the cached list is usually seconds old and unread count is live.
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && entries === null) void loadInbox(folder);
      return next;
    });
  };

  const switchFolder = (f: Folder) => {
    setFolder(f);
    setEntries(null);
    void loadInbox(f);
  };

  const deleteOne = async (id: string) => {
    // Optimistic: drop it locally, put it back if the API fails.
    const prev = entries;
    setEntries((list) => (list ? list.filter((e) => e.id !== id) : list));
    try {
      await api.deleteMessages([id]);
    } catch {
      setEntries(prev);
    }
  };

  const restoreOne = async (id: string) => {
    const prev = entries;
    setEntries((list) => (list ? list.filter((e) => e.id !== id) : list));
    try {
      await api.restoreMessages([id]);
    } catch {
      setEntries(prev);
    }
  };

  const markOne = async (id: string) => {
    // Optimistic: flip readAt locally, decrement unread, reconcile on failure.
    setEntries((prev) =>
      prev
        ? prev.map((e) => (e.id === id && !e.readAt ? { ...e, readAt: new Date().toISOString() } : e))
        : prev,
    );
    setUnread((n) => Math.max(0, n - 1));
    try {
      await api.markMessagesRead([id]);
    } catch {
      // On failure, re-fetch to reconcile. Rare; not worth a toast.
      void loadInbox();
    }
  };

  const markAll = async () => {
    const prev = entries;
    setEntries((list) => (list ? list.map((e) => ({ ...e, readAt: e.readAt ?? new Date().toISOString() })) : list));
    setUnread(0);
    try {
      await api.markAllMessagesRead();
    } catch {
      setEntries(prev);
      void loadInbox();
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        data-testid="inbox-bell"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread > 0 ? (isEn ? `Inbox · ${unread} unread` : `Inbox · ${unread} não lidas`) : 'Inbox'}
        title={unread > 0 ? (isEn ? `Inbox · ${unread} unread` : `Inbox · ${unread} não lidas`) : 'Inbox'}
        className={cn(
          'pressable relative flex h-11 w-11 items-center justify-center rounded-full border border-line',
          'bg-surface-2 text-ink-muted hover:text-ink hover:bg-surface-3 transition-colors duration-150',
          'focus-ring',
        )}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          {/* Envelope outline + flap. Linear strokes, no fill — sits
              on the ink-muted token so hover flips to ink via CSS. */}
          <rect
            x="2.5"
            y="4.5"
            width="15"
            height="11"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <path
            d="M3 6l7 5 7-5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {unread > 0 && (
          <span
            className={cn(
              // listen-to-JP-fix · 2026-05-16
              // pulled inside the button (positive inset, not corner anchor)
              // and shrunk to feel proportional. Previous version bled
              // outside the icon — JP screenshot caught it.
              'absolute right-1 top-1 inline-flex h-[14px] min-w-[14px] items-center justify-center',
              'rounded-full bg-err px-[3px] text-[9px] font-semibold leading-none text-white',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        // listen-to-JP-fix · 2026-05-20 — added z-50 so the inbox panel
        // outranks earlier `relative` siblings in the TopBar (notably
        // ProjectSwitcher, which used to clip its right edge under us).
        // All TopBar dropdowns now share z-50.
        <div
          role="menu"
          data-testid="inbox-bell-panel"
          style={{ transformOrigin: 'top right' }}
          className={cn(
            'absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-line z-50',
            'bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in',
          )}
        >
          <div className="border-b border-line">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">
                  {folder === 'sent'
                    ? (isEn ? 'Sent' : 'Enviadas')
                    : folder === 'deleted'
                      ? (isEn ? 'Deleted' : 'Apagadas')
                      : (isEn ? 'Inbox' : 'Caixa de entrada')}
                </p>
                <p className="text-[11px] text-ink-subtle">
                  {folder === 'inbox'
                    ? unread > 0
                      ? (isEn ? `${unread} unread` : `${unread} não lida${unread > 1 ? 's' : ''}`)
                      : (isEn ? 'All caught up' : 'Tudo em dia')
                    : folder === 'deleted'
                      ? (isEn ? 'Deleted items auto-clear after 30 days' : 'Apagadas auto-limpam em 30 dias')
                      : (isEn ? 'Messages you sent' : 'Mensagens que você mandou')}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setComposeOpen(true);
                  }}
                  className="pressable inline-flex items-center gap-1 rounded-md bg-ink px-2 py-1 text-[11px] font-medium text-surface hover:bg-ink/95"
                  aria-label={isEn ? 'New message' : 'Nova mensagem'}
                >
                  <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                  {isEn ? 'New' : 'Nova'}
                </button>
                {folder === 'inbox' && entries && entries.some((e) => !e.readAt) && (
                  <button
                    type="button"
                    onClick={markAll}
                    className="pressable rounded-md px-2 py-1 text-[11px] font-medium text-ink-muted hover:bg-surface-3 hover:text-ink"
                  >
                    {isEn ? 'Mark all' : 'Marcar todas'}
                  </button>
                )}
              </div>
            </div>
            {/* Folder tabs — simple underline picker. Stays on one line
                at desktop widths; wraps gracefully on mobile. */}
            <div className="flex items-center gap-1 px-2 pb-1.5">
              {(['inbox', 'sent', 'deleted'] as Folder[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => switchFolder(f)}
                  className={cn(
                    'pressable rounded-md px-2 py-1 text-[11px] font-medium',
                    folder === f
                      ? 'bg-surface-3 text-ink'
                      : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                  )}
                >
                  {f === 'inbox' ? (isEn ? 'Inbox' : 'Entrada') : f === 'sent' ? (isEn ? 'Sent' : 'Enviadas') : (isEn ? 'Deleted' : 'Apagadas')}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading && (
              <div className="space-y-2 px-3 py-3" data-testid="inbox-bell-loading">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full opacity-80" />
                <Skeleton className="h-12 w-full opacity-60" />
              </div>
            )}
            {!loading && entries && entries.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-ink-muted">
                {isEn ? 'Nothing here yet.' : 'Nada por aqui ainda.'}
                <br />
                <span className="text-[11px] text-ink-subtle">
                  {isEn ? 'Messages, alerts, and notices appear here.' : 'Mensagens, alertas e avisos aparecem aqui.'}
                </span>
              </div>
            )}
            {!loading &&
              entries &&
              entries.map((entry) => (
                <InboxRow
                  key={entry.id}
                  entry={entry}
                  folder={folder}
                  isEn={isEn}
                  onMarkRead={() => markOne(entry.id)}
                  onOpenThread={() => {
                    setThreadId(entry.id);
                    setOpen(false);
                  }}
                  onDelete={() => deleteOne(entry.id)}
                  onRestore={() => restoreOne(entry.id)}
                />
              ))}
          </div>

          <div className="border-t border-line px-3 py-2">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="block rounded-md px-2 py-1.5 text-center text-[11px] font-medium text-ink-muted hover:bg-surface-3 hover:text-ink"
            >
              {isEn ? 'Notification preferences' : 'Preferências de notificação'}
            </Link>
          </div>
        </div>
      )}

      <Compose
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={() => {
          // Optimistic: clear cache so the sent message surfaces as the
          // sender sees their own copy (we also land in the "user" kind
          // fanout for recipients).
          setEntries(null);
          void loadInbox(folder);
        }}
      />
      <Thread
        open={threadId !== null}
        messageId={threadId}
        onClose={() => setThreadId(null)}
        onChanged={() => {
          // After a reply, the inbox might gain a new row when this
          // user is also among the recipients; refresh.
          setEntries(null);
          void loadInbox(folder);
        }}
      />
    </div>
  );
}

function InboxRow({
  entry,
  folder,
  isEn,
  onMarkRead,
  onOpenThread,
  onDelete,
  onRestore,
}: {
  entry: InboxEntry;
  folder: Folder;
  isEn: boolean;
  onMarkRead: () => void;
  onOpenThread: () => void;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const unread = !entry.readAt;
  const when = relativeTime(entry.createdAt, isEn);

  // kind-specific accent + label. User messages look like normal DMs
  // with just the author name and relative time; agent_alert and system
  // get a small coloured tag so the user can skim by severity.
  const tag = (() => {
    if (entry.kind === 'agent_alert') {
      const sev = (entry.metadata?.suspicionLevel as string | undefined) ?? 'suspicious';
      if (sev === 'critical') return { label: isEn ? 'critical' : 'crítico', cls: 'bg-err/15 text-err border-err/30' };
      return { label: isEn ? 'alert' : 'alerta', cls: 'bg-warn/15 text-warn border-warn/30' };
    }
    if (entry.kind === 'system') {
      return { label: isEn ? 'system' : 'sistema', cls: 'bg-brand-400/15 text-brand-600 border-brand-400/30' };
    }
    return null;
  })();

  const who =
    entry.kind === 'agent_alert'
      ? 'HOUSE'
      : entry.kind === 'system'
        ? (isEn ? 'System' : 'Sistema')
        : (entry.author?.fullName ?? (isEn ? 'Someone' : 'Alguém'));

  // Inbox preview is single-line truncated, so we collapse markdown
  // links to their display text — `[text](url)` would otherwise
  // appear as raw noise. The clickable hyperlink lives in the full
  // thread view (rendered by MessageBodyRender).
  const previewBody = messageBodyPlain(entry.body);
  const preview = entry.subject ? `${entry.subject} — ${previewBody}` : previewBody;

  // Wave 47 — when another admin/PM has already approved/rejected the
  // lead or signup, replace the full body with a compact resolution
  // line so this row doesn't compete for attention.
  const resolution = entry.metadata?.resolution as
    | 'approved'
    | 'rejected'
    | 'cancelled'
    | undefined;
  const resolvedBy = entry.metadata?.resolvedByName as string | null | undefined;
  const resolvedTag =
    resolution === 'approved'
      ? { label: isEn ? `approved${resolvedBy ? ` by ${resolvedBy}` : ''}` : `aprovado${resolvedBy ? ` por ${resolvedBy}` : ''}`, cls: 'text-ok' }
      : resolution === 'rejected'
        ? { label: isEn ? `rejected${resolvedBy ? ` by ${resolvedBy}` : ''}` : `rejeitado${resolvedBy ? ` por ${resolvedBy}` : ''}`, cls: 'text-err' }
        : resolution === 'cancelled'
          ? { label: isEn ? 'cancelled' : 'cancelado', cls: 'text-ink-subtle' }
          : null;

  return (
    <div
      className={cn(
        'group relative flex w-full items-start gap-3 border-b border-line/60 px-4 py-3 text-left transition-colors',
        'hover:bg-surface-3',
        unread && folder === 'inbox' && 'bg-surface-2',
      )}
      onClick={() => {
        if (unread && folder === 'inbox') onMarkRead();
        onOpenThread();
      }}
    >
      {/* Unread dot. Occupies the same 8px column when read — keeps
          rows perfectly aligned whether or not the dot is visible. */}
      <span
        aria-hidden
        className={cn(
          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
          unread ? 'bg-brand-500' : 'bg-transparent',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={cn('truncate text-sm', unread ? 'font-semibold text-ink' : 'text-ink')}>
            {who}
          </span>
          {tag && (
            <span
              className={cn(
                'shrink-0 rounded-full border px-1.5 text-[9px] font-medium uppercase tracking-wider',
                tag.cls,
              )}
            >
              {tag.label}
            </span>
          )}
          <span className="ml-auto shrink-0 text-[10px] text-ink-subtle">{when}</span>
        </div>
        <p className={cn('mt-0.5 line-clamp-2 text-[13px] leading-relaxed', unread ? 'text-ink' : 'text-ink-muted')}>
          {preview}
        </p>
        {resolvedTag && (
          <p className={cn('mt-1 text-[10px] uppercase tracking-wider', resolvedTag.cls)}>
            {resolvedTag.label}
          </p>
        )}
      </div>
      {/* Trailing action — only visible on hover. Delete from inbox/sent,
          restore from the deleted folder. Positioned absolute so the
          row text doesn't jump when hover reveals it. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (folder === 'deleted') onRestore();
          else onDelete();
        }}
        aria-label={folder === 'deleted' ? (isEn ? 'Restore' : 'Restaurar') : (isEn ? 'Delete' : 'Apagar')}
        title={folder === 'deleted' ? (isEn ? 'Restore' : 'Restaurar') : (isEn ? 'Delete' : 'Apagar')}
        className={cn(
          'pressable absolute right-2 top-3 rounded-md p-1 text-ink-subtle opacity-0 transition-opacity',
          'group-hover:opacity-100 hover:bg-surface-2 hover:text-ink focus:opacity-100',
        )}
      >
        {folder === 'deleted' ? (
          <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8a5 5 0 1 1 1.46 3.54" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
            <path d="M3 4v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        ) : (
          <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 4h10M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

/** Relative time, locale-aware. "now / 3min / 2h / yesterday / Apr 5". */
function relativeTime(iso: string, isEn: boolean): string {
  const date = parseServerDate(iso);
  if (!date) return '';
  const then = date.getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return isEn ? 'now' : 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return isEn ? 'yesterday' : 'ontem';
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString(isEn ? 'en-US' : 'pt-BR', { day: '2-digit', month: 'short' });
}
